import { useEffect, useState } from 'react';
import { CloudIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Modal, BTN_PRIMARY_SM, type User } from '@trinetra/shared';
import { userService } from '../../services/user.service';
import { getNodes } from '../../services/api.service';

interface NodeAccessDialogProps {
  user: User;
  onClose: () => void;
}

interface NodeInfo {
  nodeId: string;
  name: string;
  status: 'online' | 'offline';
}

interface Grant {
  node_id: string;
  node_role_id: string | null;
  node_role_name: string | null;
  status: 'pending' | 'provisioned' | 'broken' | 'revoking';
}

const GRANT_STATUS_STYLES: Record<string, string> = {
  provisioned: 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400',
  pending: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400',
  broken: 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400',
  revoking: 'bg-muted text-muted-foreground',
};

/**
 * Per-user node access management. A grant = (node, node's OWN role) — it
 * provisions the cloud user onto that node as a non-login user used for
 * RBAC and alert assignment. admin/super_admin users have implicit access
 * to all nodes and need no grants.
 */
export default function NodeAccessDialog({ user, onClose }: NodeAccessDialogProps) {
  const isAdmin = user.role === 'admin' || user.role === 'super_admin';
  const [nodes, setNodes] = useState<NodeInfo[]>([]);
  const [grants, setGrants] = useState<Grant[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyNode, setBusyNode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [nodeData, access] = await Promise.all([getNodes(), userService.getNodeAccess(user.id)]);
      const nodeList: NodeInfo[] = (nodeData?.nodes || nodeData || []).map((n: any) => ({
        nodeId: n.nodeId ?? n.node_id,
        name: n.name,
        status: n.status,
      }));
      setNodes(nodeList);
      setGrants(access.grants || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load site access');
    } finally {
      setLoading(false);
    }
  }

  async function grant(nodeId: string) {
    setBusyNode(nodeId);
    setError(null);
    try {
      // No node-role selection: the user is provisioned on the node mirroring
      // their cloud role. Camera/group access is assigned separately in the
      // Access Control page (explicit scope), not derived from a node role.
      await userService.grantNodeAccess(user.id, nodeId);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to grant access');
    } finally {
      setBusyNode(null);
    }
  }

  async function revoke(nodeId: string) {
    setBusyNode(nodeId);
    setError(null);
    try {
      await userService.revokeNodeAccess(user.id, nodeId);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to revoke access');
    } finally {
      setBusyNode(null);
    }
  }

  const grantByNode = new Map(grants.map((g) => [g.node_id, g]));

  return (
    <Modal open onClose={onClose} size="lg" title={`Site Access — ${user.name || user.email}`}>
      <div className="p-5">
        <p className="mb-4 text-[12px] text-muted-foreground">
          Granting access provisions this user on the node (no local login). Assign the cameras and groups they can see in the Access Control page.
        </p>

        {error && (
          <div className="mb-3 flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-[12px] text-red-700 dark:bg-red-900/20 dark:text-red-400">
            <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />
            {error}
          </div>
        )}

        {isAdmin && (
          <div className="mb-3 flex items-center gap-2 rounded-lg bg-sky-50 px-3 py-2 text-[12px] text-sky-700 dark:bg-sky-900/20 dark:text-sky-400">
            <CloudIcon className="h-4 w-4 shrink-0" />
            This user is <b className="mx-1">{user.role?.replace(/_/g, ' ')}</b> — access to all sites is implicit and cannot be revoked per site.
          </div>
        )}

        {loading ? (
          <p className="py-8 text-center text-[13px] text-muted-foreground">Loading…</p>
        ) : nodes.length === 0 ? (
          <p className="py-8 text-center text-[13px] text-muted-foreground">No sites registered.</p>
        ) : (
          <div className="divide-y divide-border rounded-lg border border-border">
            {nodes.map((node) => {
              const g = grantByNode.get(node.nodeId);
              return (
                <div key={node.nodeId} className="flex flex-wrap items-center gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`h-2 w-2 rounded-full ${node.status === 'online' ? 'bg-green-500' : 'bg-muted-foreground'}`} />
                      <span className="truncate text-[13px] font-medium text-foreground">{node.name}</span>
                      <span className="text-[11px] text-muted-foreground">({node.nodeId})</span>
                    </div>
                    {g && (
                      <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
                        <span className={`rounded-full px-2 py-0.5 font-semibold ${GRANT_STATUS_STYLES[g.status] || ''}`}>{g.status}</span>
                      </div>
                    )}
                  </div>

                  {isAdmin ? (
                    <span className="text-[12px] text-muted-foreground">All sites (by role)</span>
                  ) : g ? (
                    <button
                      onClick={() => revoke(node.nodeId)}
                      disabled={busyNode === node.nodeId}
                      className="inline-flex h-8 items-center rounded-lg px-3 text-[12px] font-medium text-destructive transition-colors hover:bg-destructive/10 disabled:opacity-50"
                    >
                      {busyNode === node.nodeId ? 'Revoking…' : 'Revoke'}
                    </button>
                  ) : (
                    <button
                      onClick={() => grant(node.nodeId)}
                      disabled={busyNode === node.nodeId}
                      className={BTN_PRIMARY_SM}
                    >
                      {busyNode === node.nodeId ? 'Granting…' : 'Grant access'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
}
